// src/components/FindFriends.tsx
// FIXES:
// - Line 57: Handle undefined result.user
// - Line 82: Fix connection_status type ('accepted' -> 'connected')
// - Lines 100, 133, 219: Update connection_status comparisons
// - Removed debounce and onIonInput to fix text "eating" issue - search now only triggers on Enter key

import React, { useState, useEffect } from 'react';
import {
  IonSearchbar,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
  IonAvatar,
  IonBadge,
} from '@ionic/react';
import { personAddOutline, checkmarkCircle, timeOutline, peopleOutline } from 'ionicons/icons';
import { searchUserByUsername, SearchResult } from '../services/userSearchService';
import { sendConnectionRequest } from '../services/connectionsService';
import { AvatarPreview } from './avatar/AvatarPreview';

const FindFriends: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');


  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setErrorMessage('Please enter a username');
      setSearchResult(null);
      return;
    }

    setSearching(true);
    setErrorMessage('');
    setSuccessMessage('');
    setSearchResult(null);

    const result = await searchUserByUsername(searchQuery);

    setSearching(false);

    if (result.success && result.user) {
      // FIX: Handle undefined by checking result.user exists
      setSearchResult(result.user);
    } else {
      setErrorMessage(result.error || 'User not found or an error occurred.');
    }
  };

  const handleSendRequest = async () => {
    if (!searchResult) return;

    setSending(true);
    setSuccessMessage('');
    setErrorMessage('');

    const result = await sendConnectionRequest(searchResult.id);

    setSending(false);

    if (result.success) {
      const newSuccessMessage = `Connection request sent successfully!${
        result.match?.id ? ' It was an instant match!' : ''
      }`;
      setSuccessMessage(newSuccessMessage);

      // FIX: Change 'accepted' to 'connected' to match SearchResult type
      setSearchResult(prev => {
        if (!prev) return null;
        return {
          ...prev,
          connection_status: result.match?.id ? 'connected' : 'pending_sent',
          ...(result.match?.id && { match_id: result.match.id }),
        };
      });
    } else {
      setErrorMessage(result.error || 'Failed to send connection request.');
    }
  };

  const getButtonContent = () => {
    if (!searchResult) return null;

    // FIX: Remove 'rejected' comparison as it's not a valid status
    if (searchResult.connection_status === 'none') {
      return (
        <IonButton
          expand="block"
          onClick={handleSendRequest}
          disabled={sending || searching || !!successMessage}
          color="primary"
          className="ion-margin-top"
        >
          <IonIcon slot="start" icon={personAddOutline} />
          {sending ? <IonSpinner name="crescent" /> : 'Send Connection Request'}
        </IonButton>
      );
    }

    if (searchResult.connection_status === 'pending_sent') {
      return (
        <IonButton expand="block" fill="outline" color="medium" className="ion-margin-top" disabled>
          <IonIcon slot="start" icon={timeOutline} />
          Request Pending
        </IonButton>
      );
    }

    if (searchResult.connection_status === 'pending_received') {
      return (
        <IonButton expand="block" fill="outline" color="primary" className="ion-margin-top" disabled>
          <IonIcon slot="start" icon={peopleOutline} />
          Request Received
        </IonButton>
      );
    }

    // FIX: Change 'accepted' to 'connected'
    if (searchResult.connection_status === 'connected') {
      return (
        <IonButton expand="block" fill="outline" color="success" className="ion-margin-top" disabled>
          <IonIcon slot="start" icon={checkmarkCircle} />
          Connected
        </IonButton>
      );
    }

    // Handle 'blocked' status
    if (searchResult.connection_status === 'blocked') {
      return null; // Don't show any button for blocked users
    }

    return null;
  };

  return (
    <div className="find-friends-container">
      <IonSearchbar
        value={searchQuery}
        onIonChange={(e) => setSearchQuery(e.detail.value!)}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        placeholder="Enter username to search"
        animated
        className="ion-margin-bottom"
      />

      {/* Loading Spinner */}
      {searching && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <IonSpinner name="crescent" />
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <IonText color="danger">
          <p style={{ textAlign: 'center', padding: '10px 20px', margin: '0' }}>{errorMessage}</p>
        </IonText>
      )}

      {/* Success Message */}
      {successMessage && (
        <IonCard color="success" className="ion-margin-top">
          <IonCardContent>
            <IonText color="light">
              <h2 style={{ textAlign: 'center', margin: '0' }}>{successMessage}</h2>
            </IonText>
          </IonCardContent>
        </IonCard>
      )}

      {/* Search Result Card */}
      {searchResult && !searching && !errorMessage && (
        <IonCard className="ion-margin-top">
          <IonCardContent className="ion-text-center">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                marginBottom: '16px',
              }}
            >
              <IonAvatar style={{ width: '60px', height: '60px' }}>
                {searchResult.avatar_url ? (
                  <img
                    src={searchResult.avatar_url}
                    alt={searchResult.display_name || searchResult.username}
                  />
                ) : searchResult.avatar ? (
                    <AvatarPreview
                      avatarData={searchResult.avatar}
                      size={60}
                      userId={searchResult.id}
                      isAdmin={searchResult.is_admin || false}
                    />
                ) : (
                  <AvatarPreview size={60} userId={searchResult.id} />
                )}
              </IonAvatar>
              <div>
                <h2>{searchResult.display_name || searchResult.username}</h2>
                {searchResult.display_name && (
                  <IonText color="medium">
                    <p>@{searchResult.username}</p>
                  </IonText>
                )}
                {/* FIX: Change 'accepted' to 'connected' */}
                {searchResult.connection_status === 'connected' && (
                  <IonBadge color="success">Connected</IonBadge>
                )}
                {searchResult.connection_status === 'pending_sent' && (
                  <IonBadge color="warning">Request Sent</IonBadge>
                )}
                {searchResult.connection_status === 'pending_received' && (
                  <IonBadge color="primary">Wants to Connect</IonBadge>
                )}
              </div>
            </div>

            {getButtonContent()}

            {searchResult.connection_status === 'pending_received' && (
              <IonText color="medium">
                <p style={{
                  fontSize: '13px',
                  textAlign: 'center',
                  margin: '12px 0 0',
                }}>
                  Check your Requests tab to accept their connection request!
                </p>
              </IonText>
            )}
          </IonCardContent>
        </IonCard>
      )}

      {/* Empty State */}
      {!searching && !searchResult && !errorMessage && !successMessage && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--ion-color-medium)'
        }}>
          <IonIcon
            icon={personAddOutline}
            style={{ fontSize: '64px', marginBottom: '16px' }}
          />
          <p>Search for your friends by their username</p>
        </div>
      )}
    </div>
  );
};

export default FindFriends;